// The single source of the workbench navigation (portal workbench spec,
// docs/superpowers/specs/2026-09-29-portal-workbench-design.md): groups,
// items, routes, who sees each item, and where a user lands. A pure function
// of (claims, scope), with no JSX.
//
// Display only. Hiding an item is UX: every route still hits the same
// backend checks (portal/backend/lib/permissions.mjs), which stay the only
// enforcement point. The active item comes from the route path alone, so two
// items can never be current at once (the ENTRAID-267 class of bug).
//
// Who sees an item (ENTRAID-358, spec 2026-11-03-permission-bitmask-table-design.md 5.6): a can-shaped check over the
// shared permission table (portal/shared/permissions.mjs), the same table the API enforces, with the principals the
// /me access summary gives for the scope and the facts the SPA knows. Pinned equal to the role-based navigation it
// replaced by navigation-personas.test.js.

import { effectiveAccess } from './tenant-access.js';
import { ACTIONS, OPERATOR, effectiveMask } from '../../../shared/permissions.mjs';
import { ACCESS_REVIEWER_ROLE, ADMIN_ROLE, AUDITOR_ROLE, DEVELOPER_ROLE, QLIK_CREDENTIALS_ROLE, VIEWER_ROLE } from '../../../shared/solution.mjs';

/**
 * @typedef {{ isOperator: boolean, customerId: string|null, admin: boolean, developer: boolean, qlikCredentials: boolean, auditor: boolean, viewer: boolean, reviewer: boolean }} NavClaims
 * @typedef {{ kind: 'all' } | { kind: 'customer', customerId: string }} Scope
 * @typedef {{ id: string, label: string, to: string, keywords?: string[] }} NavItem
 * @typedef {{ id: string, label: string, items: NavItem[] }} NavGroup
 */

/** The operators' scope: every customer. */
export const ALL_CUSTOMERS = Object.freeze({ kind: 'all' });

/** One customer's scope. */
export function customerScope(customerId) {
  return Object.freeze({ kind: 'customer', customerId });
}

export const validId = (id) => typeof id === 'string' && id.length > 0;
const ownsScope = (c, scope) => scope?.kind === 'customer' && validId(c.customerId) && c.customerId === scope.customerId;

// The NavClaims role flags and the tenant role each one stands for (portal/shared/solution.mjs).
const CLAIM_ROLES = Object.freeze([
  ['admin', ADMIN_ROLE],
  ['developer', DEVELOPER_ROLE],
  ['qlikCredentials', QLIK_CREDENTIALS_ROLE],
  ['auditor', AUDITOR_ROLE],
  ['viewer', VIEWER_ROLE],
  ['reviewer', ACCESS_REVIEWER_ROLE],
]);

/**
 * The principals of the permission table these claims hold in this scope: the tenant roles only on the customer they
 * belong to (never on another customer), plus the operator in every scope. The API scopes the TEST e2e operator further;
 * /me says so where it matters (access.canExecuteFleetCode).
 */
function principalsIn(c, scope) {
  const held = ownsScope(c, scope) ? CLAIM_ROLES.filter(([flag]) => c[flag] === true).map(([, role]) => role) : [];
  return c.isOperator === true ? [...held, OPERATOR] : held;
}

/**
 * The facts the SPA knows, for display only. `operator` is the claims' own flag. /me already sets `reviewer`, `viewer`
 * and `auditor` only while the role is registered (C1, C2), and the auditor's live grant is checked by every Compliance route itself, so a role
 * /me reports counts here. The facts only the API can read (customer zero, the Watch tier, the central IdP admin lookup)
 * stay false, and `staff` is false: no navigation item depends on them, so the navigation is the same as before.
 */
const displayFacts = (c) => ({
  operator: c.isOperator === true,
  staff: false,
  customerZero: false,
  watchTierAllows: false,
  auditorGrantLive: true,
  idpAdmin: false,
  flags: { reviewer: c.reviewer === true, viewer: c.viewer === true, auditor: c.auditor === true },
});

/** True when these claims hold `action` on `resource` in this scope, by the shared permission table. Display only. */
function can(c, scope, resource, action) {
  const required = ACTIONS[action];
  return (effectiveMask(resource, principalsIn(c, scope), displayFacts(c)) & required) === required;
}

/** A visibility check: the claims hold any one of these [resource, action] pairs in this scope. */
const canAny = (...checks) => (c, scope) => checks.some(([resource, action]) => can(c, scope, resource, action));

// The Access Reviews reviewer role alone (spec 2026-10-29): "Your reviews" is all it opens, so Access reviews goes
// straight there (access-reviews/mine). With the admin, the operator or the viewer role the page itself is the entry.
const readsAccessReviews = canAny(['accessReviews', 'read']);
const reviewerOnly = (c, scope) => !readsAccessReviews(c, scope) && can(c, scope, 'accessReviews.mine', 'read');

/**
 * True when this user may load a customer's record (GET /customers/{id}):
 * operators, and that customer's own admin. A developer or a qlik-credentials
 * holder never can; they get their sections only.
 */
export function readsCustomerRecord(claims, customerId) {
  return validId(customerId) && can(claims ?? {}, customerScope(customerId), 'customer', 'read');
}

/**
 * True when this user may load the record Overview and Security are built on
 * (GET /customers/{id}) and their score trend (GET .../runs, summary view):
 * everyone readsCustomerRecord admits, plus that customer's viewer role, which
 * the API answers with a narrowed record.
 */
export function readsOverviewRecord(claims, customerId) {
  return validId(customerId) && can(claims ?? {}, customerScope(customerId), 'customer.summary', 'read');
}

/**
 * True when this user reaches the customer only as its read-only viewer (not an
 * operator, not its admin): Overview and Security render with no action and no
 * link to a page it cannot open. Display only; the API enforces it.
 */
export function viewerOnly(claims, customerId) {
  if (!validId(customerId)) return false;
  const c = claims ?? {};
  const scope = customerScope(customerId);
  return !can(c, scope, 'customer', 'read') && can(c, scope, 'customer.summary', 'read');
}

// Sidebar order (ENTRAID-347 redesign, ia-proposal section 2, the A5 menu): Monitor answers, Configure changes,
// Implementation shows what runs and how to build on it. Sentence case labels. Roles combine: the sidebar shows the union. `keywords` are the old names and the
// parts a page holds, so Quick find still finds a page by what it used to be called (display only, never a route).
const CUSTOMER_GROUPS = Object.freeze([
  {
    id: 'monitor',
    label: 'Monitor',
    items: [
      // The viewer role (2026-10-25) sees these and nothing else.
      { id: 'overview', label: 'Overview', visible: canAny(['customer.summary', 'read']), keywords: ['home', 'score', 'changes since yesterday'] },
      { id: 'security', label: 'Security', visible: canAny(['customer.summary', 'read']), keywords: ['checks', 'findings', 'secure score', 'posture'] },
      // License Insights (Hugo Andrade, 2026-10-26): every tier; the viewer reads aggregates only (no names, no editing).
      { id: 'licenses', label: 'Licenses', visible: canAny(['licenses', 'read']), keywords: ['license insights', 'cost', 'savings', 'waste', 'seats', 'subscriptions', 'SKU', 'products'] },
      // Access reviews (spec 2026-10-26, section 5): campaigns on professional and up, findings on every tier; the viewer
      // and the operator read counts only (no names, no Setup, no actions). Display only: the API decides.
      { id: 'access-reviews', label: 'Access reviews', visible: canAny(['accessReviews', 'read'], ['accessReviews.mine', 'read']), sub: (c, scope) => (reviewerOnly(c, scope) ? 'mine' : null), keywords: ['access review', 'recertification', 'certification', 'attestation', 'privileged roles', 'admin roles', 'least privilege', 'quarterly review', 'campaign'] },
      // The Watch events and flagged cases, moved out of Notifications (plan C3). The same access Notifications had; an
      // operator account sees Watch health only (the portal refuses it Watch content).
      { id: 'activity', label: 'Activity', visible: canAny(['watch.state', 'read']), keywords: ['watch', 'events', 'flagged', 'cases'] },
      // ENTRAID-341: Reports hub (was Compliance). Operators see the health-only view (compliance.audit); the admin and the
      // auditor the controls; an auditor sees nothing else. The developer reads report files, not this hub.
      { id: 'reports', label: 'Reports', visible: canAny(['compliance.controls', 'read'], ['compliance.audit', 'read']), keywords: ['compliance', 'controls', 'attestations', 'evidence'] },
    ],
  },
  {
    id: 'configure',
    label: 'Configure',
    items: [
      // Sources, Collections and the schedule on one page (plan C5).
      { id: 'data-collection', label: 'Data collection', visible: canAny(['runs', 'read']), keywords: ['sources', 'collections', 'collect now', 'run now', 'schedule', 'retention'] },
      // Its first tab is in the path (/notifications/rules): /notifications itself is the old Watch feed, now Activity.
      { id: 'notifications', label: 'Notifications', visible: canAny(['notifications', 'read']), sub: 'rules', keywords: ['rules', 'recipients', 'delivery', 'sending domain', 'teams', 'send log', 'alerts'] },
      { id: 'qlik-cloud', label: 'Qlik Cloud', visible: canAny(['qlik', 'read']), keywords: ['qlik'] },
      { id: 'people', label: 'People', visible: canAny(['people', 'read']), keywords: ['roles', 'access requests', 'guardians'] },
      // Company Settings and Deployment on one page, with the same access (plan C6).
      { id: 'settings', label: 'Settings', visible: canAny(['setup', 'read']), keywords: ['company settings', 'branding', 'deployment', 'consent', 'microsoft connection', 'danger zone'] },
    ],
  },
  {
    id: 'implementation',
    label: 'Implementation',
    items: [
      // Implementation footprint (spec 2026-10-29): what onGuard runs in this tenant and AWS account. The developer and the
      // auditor read it too (the auditor gets this and Reports, never the Data reference). Display only: the API decides.
      // 'develop' keeps the old group name findable in Quick find.
      { id: 'implementation-footprint', label: 'Implementation footprint', visible: canAny(['footprint', 'read']), keywords: ['develop', 'footprint', 'what runs', 'schedule', 's3', 'paths', 'evidence'] },
      { id: 'data-reference', label: 'Data reference', visible: canAny(['dataReference', 'read']), keywords: ['fields', 'schema', 'guide'] },
    ],
  },
]);

// Operations: operators only, at the top of the sidebar in every scope (a customer selected or All customers), so the
// Customers page ("Add customer") and the Fleet overview are always one click away. Hidden in "View as client" (toNavClaims
// clears isOperator there). Quick find reads the keywords.
const OPERATIONS_GROUP = Object.freeze({
  id: 'operations',
  label: 'Operations',
  items: [
    { id: 'fleet-overview', label: 'Fleet overview', to: '/fleet', keywords: ['fleet', 'health', 'all customers', 'overview'] },
    { id: 'customers', label: 'Customers', to: '/customers', keywords: ['add customer', 'new customer', 'create customer', 'onboard', 'customer list'] },
  ],
});

// The All customers scope: operators only, below Operations.
const FLEET_GROUPS = Object.freeze([
  {
    id: 'implementation',
    label: 'Implementation',
    items: [
      { id: 'data-reference', label: 'Data reference', to: '/data-reference' },
    ],
  },
]);

const operationsGroup = () => ({
  id: OPERATIONS_GROUP.id,
  label: OPERATIONS_GROUP.label,
  items: OPERATIONS_GROUP.items.map(({ id, label, to, keywords }) => ({ id, label, to, keywords: [...keywords] })),
});

/** Every customer section id, in sidebar order; also the route segment. */
export const CUSTOMER_SECTIONS = Object.freeze(CUSTOMER_GROUPS.flatMap((g) => g.items.map((i) => i.id)));

const SECTION_LABELS = new Map(CUSTOMER_GROUPS.flatMap((g) => g.items.map((i) => [i.id, i.label])));

/** The sidebar label of a customer section, which is also its page title (ENTRAID-347 PageHeader). */
export function sectionLabel(sectionId) {
  return SECTION_LABELS.get(sectionId) ?? sectionId;
}

const FLEET_PATHS = new Map([...OPERATIONS_GROUP.items, ...FLEET_GROUPS.flatMap((g) => g.items)].map((i) => [i.to, i.id]));

// The sections of the destination scope itself: Operations is the same everywhere, so it never counts as "the same section".
const scopeItems = (nav) => nav.groups.filter((g) => g.id !== OPERATIONS_GROUP.id).flatMap((g) => g.items);

/** The route of one customer section. */
export function customerSectionPath(customerId, sectionId) {
  return `/customers/${encodeURIComponent(customerId)}/${sectionId}`;
}

// A sidebar item's link: the section, or its first tab when that tab is addressed by the path.
const itemPath = (customerId, item, c = {}, scope = null) => {
  const sub = typeof item.sub === 'function' ? item.sub(c, scope) : item.sub;
  return `${customerSectionPath(customerId, item.id)}${sub ? `/${sub}` : ''}`;
};

/** True when these claims reach this customer's Access reviews only through "Your reviews" (the reviewer role alone). */
export function reviewsOnly(claims, customerId) {
  return validId(customerId) && reviewerOnly(claims ?? {}, customerScope(customerId));
}

/**
 * The claims the UI already derives from GET /me (lib/tenant-access.js),
 * with an operator's "View as client" applied: while viewing as a client the
 * operator is that customer's admin, not an operator.
 *
 * @param {object|null} me - the GET /me body
 * @param {{ customerId: string, companyName?: string }|null} impersonating
 * @param {string|null} [scopeCustomerId] - the customer of the current page: a user of several customers gets the
 *   roles of that one (ENTRAID-307)
 * @returns {NavClaims}
 */
export function toNavClaims(me, impersonating, scopeCustomerId = null) {
  const access = effectiveAccess(me, impersonating, scopeCustomerId);
  return Object.freeze({
    isOperator: !impersonating?.customerId && me?.user?.isOperator === true,
    customerId: access.customerId,
    admin: access.admin === true,
    developer: access.developer === true,
    qlikCredentials: access.qlikCredentials === true,
    auditor: access.auditor === true,
    viewer: access.viewer === true,
    reviewer: access.reviewer === true,
  });
}

/**
 * The sidebar for these claims in this scope. Empty groups are hidden. Operators
 * also get Operations first, in every scope. The landing is the first visible
 * customer section (or /fleet in All customers).
 *
 * @param {NavClaims|null} claims
 * @param {Scope|null} scope
 * @returns {{ groups: NavGroup[], landing: string|null }}
 */
export function navigationFor(claims, scope) {
  const c = claims ?? {};
  // Operations and All customers: the fleet (fleet.customers), which only the operator column holds.
  const operator = can(c, ALL_CUSTOMERS, 'fleet.customers', 'read');
  let groups = [];
  let landing = null;
  if (scope?.kind === 'all' && operator) {
    groups = [
      operationsGroup(),
      ...FLEET_GROUPS.map((g) => ({ id: g.id, label: g.label, items: g.items.map(({ id, label, to }) => ({ id, label, to })) })),
    ];
    landing = groups[0].items[0].to;
  } else if (scope?.kind === 'customer' && validId(scope.customerId)) {
    const customerGroups = CUSTOMER_GROUPS
      .map((g) => ({
        id: g.id,
        label: g.label,
        items: g.items
          .filter((i) => i.visible(c, scope))
          .map((i) => ({ id: i.id, label: i.label, to: itemPath(scope.customerId, i, c, scope), keywords: i.keywords ?? [] })),
      }))
      .filter((g) => g.items.length > 0);
    // The landing stays the first customer section: Operations is a way around, not where a customer scope starts.
    landing = customerGroups[0]?.items[0]?.to ?? null;
    groups = operator ? [operationsGroup(), ...customerGroups] : customerGroups;
  }
  return { groups, landing };
}

/** True when this item is in the sidebar for these claims in this scope. */
export function canSee(claims, scope, itemId) {
  return navigationFor(claims, scope).groups.some((g) => g.items.some((i) => i.id === itemId));
}

/** An operator starts in All customers; a customer user in their own tenant. */
export function homeScope(claims) {
  if (claims?.isOperator === true) return ALL_CUSTOMERS;
  if (validId(claims?.customerId)) return customerScope(claims.customerId);
  return null;
}

/** Where "/" sends this user: the first page they can use, or null (no access). */
export function landingRoute(claims) {
  const scope = homeScope(claims);
  return scope ? navigationFor(claims, scope).landing : null;
}

/**
 * The route of a customer section for this user: the section itself when
 * they can see it, else the first item they can see for that customer, else
 * their own landing, else "/" (which renders the no-access page).
 */
export function sectionTarget(claims, customerId, sectionId) {
  if (validId(customerId)) {
    const nav = navigationFor(claims, customerScope(customerId));
    const item = scopeItems(nav).find((i) => i.id === sectionId);
    if (item) return item.to;
    if (nav.landing) return nav.landing;
  }
  return landingRoute(claims) ?? '/';
}

/**
 * The scope and the active item of a path, from the path alone (never a
 * query string). Pages outside the sidebar have no scope.
 *
 * @returns {{ scope: Scope|null, itemId: string|null }}
 */
export function locate(pathname) {
  const path = String(pathname ?? '').replace(/\/+$/, '') || '/';
  if (FLEET_PATHS.has(path)) return { scope: ALL_CUSTOMERS, itemId: FLEET_PATHS.get(path) };
  const match = /^\/customers\/([^/]+)(?:\/([^/]+))?(?:\/.*)?$/.exec(path);
  if (!match) return { scope: null, itemId: null };
  let customerId;
  try {
    customerId = decodeURIComponent(match[1]);
  } catch {
    return { scope: null, itemId: null };
  }
  const itemId = match[2] && CUSTOMER_SECTIONS.includes(match[2]) ? match[2] : null;
  return { scope: customerScope(customerId), itemId };
}

/**
 * Where the customer switcher goes: the current section in the target scope
 * when the target shows it, otherwise the target's landing rule.
 */
export function switchScopeTarget(claims, pathname, target) {
  const { itemId } = locate(pathname);
  const nav = navigationFor(claims, target);
  const kept = itemId ? scopeItems(nav).find((i) => i.id === itemId) : null;
  return kept?.to ?? nav.landing ?? '/';
}
